/**
 * Admin Users page (Anshul, 2026-09-26): "admins create auditor-level accounts only
 * (never admins), and can delete accounts and reset their passwords." Route:
 * `/admin/users` (AdminRoute, App.js).
 *
 * Table styling follows CriteriaPage.js/AdminMetricsPage.js (bg-[#F8F8FF] header, white
 * card, hover row); the three actions (create/reset/delete) are each their own modal, the
 * CriteriaPage detail-panel recipe. Admin rows render no Reset/Delete buttons at all --
 * Anshul's UI rule is that a control that can only ever 403 is worse than no control
 * (backend/app/api/v1/users.py reserves both routes for CUSTOMER accounts).
 *
 * Extended the same day with a "Waiting for approval" section (self-registration,
 * Anshul: "let people be able to create auditor accounts but thay get active after
 * admin approves them") and three review fixes: the generated temporary password
 * uses crypto.getRandomValues instead of Math.random (item a), is shown once in a
 * result panel with a Copy button after create/reset rather than only ever hidden
 * (item a), and a create response that restored a soft-deleted account says so
 * explicitly rather than reading like a brand-new account (item c).
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  UserPlusIcon,
  KeyIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { usersAPI } from '../services/usersApi';
import { getApiErrorMessage } from '../utils/apiError';
import { blankNameMessage } from '../utils/personName';

const PASSWORD_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';

// No ambiguous look-alikes (0/O, 1/l/I): an admin usually reads this out loud or
// copy-pastes it to hand to the new auditor directly (there is no invite email, D25/19.2).
//
// crypto.getRandomValues, not Math.random (Anshul, 2026-09-26 review item a):
// Math.random is not a cryptographically secure source -- its output is predictable
// from a handful of samples for every JS engine in production use -- and this value
// becomes a real account's password, not a UI-only token.
function generatePassword(length = 12) {
  const randomValues = new Uint32Array(length);
  (window.crypto || window.msCrypto).getRandomValues(randomValues);
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += PASSWORD_ALPHABET[randomValues[i] % PASSWORD_ALPHABET.length];
  }
  return out;
}

function formatDate(value) {
  if (!value) return '—';
  const parsed = new Date(value.endsWith('Z') ? value : `${value}Z`);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function PasswordField({ id, value, onChange, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative mt-1">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        required
        minLength={8}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-0 pr-3 flex items-center"
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? (
          <EyeSlashIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
        ) : (
          <EyeIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
        )}
      </button>
    </div>
  );
}

/** A password shown once after a create/reset succeeds, with a Copy button (Anshul,
 * 2026-09-26 review item a): before this the password was only ever entered into a
 * hidden field the admin had to remember to reveal, so it was easy to lose before it
 * was handed to the auditor. */
function CopyableSecret({ value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard API can be unavailable (older browser, a non-HTTPS context in a
      // demo environment) -- the password is still visible in the field below for a
      // manual select-and-copy, so this is not a dead end, just a quieter one.
      toast.error('Could not copy automatically -- select the password field instead');
    }
  };

  return (
    <div className="mt-1 flex items-center gap-2">
      <input
        type="text"
        readOnly
        value={value}
        onFocus={(e) => e.target.select()}
        aria-label="Password"
        className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono bg-[#F8F8FF] focus:outline-none focus:ring-blue-500 focus:border-blue-500"
      />
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-[#F8F8FF] whitespace-nowrap"
      >
        {copied ? (
          <>
            <CheckIcon className="h-4 w-4 text-green-600" />
            Copied
          </>
        ) : (
          <>
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copy
          </>
        )}
      </button>
    </div>
  );
}

function ModalShell({ label, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        {children}
      </div>
    </div>
  );
}

function NewAuditorModal({ onClose, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(() => generatePassword());
  // Set on success; while it holds a value the modal shows the result panel instead
  // of the form (Anshul, 2026-09-26 review item a: the password is shown once here,
  // not just entered into a field the admin might never reveal again).
  const [result, setResult] = useState(null);

  const createMutation = useMutation(
    () =>
      usersAPI.create({
        email,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
      }),
    {
      onSuccess: (response) => {
        const created = response.data.user;
        const restored = Boolean(response.data.restored);
        toast.success(
          restored
            ? `${created.email} was restored with its earlier projects`
            : `${created.email} can now sign in`
        );
        setResult({ email: created.email, password, restored });
      },
      onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the account')),
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const nameError = blankNameMessage(firstName, lastName);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    createMutation.mutate();
  };

  const handleDone = () => {
    onCreated();
  };

  if (result) {
    return (
      <ModalShell label="New auditor" onClose={handleDone}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Account created</h3>
          <button type="button" onClick={handleDone} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {result.restored && (
            // Anshul, 2026-09-26 review item c: creating at a deleted address
            // silently restored the old account and its projects before this --
            // the admin must be told explicitly, not left to notice on their own.
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
              This email belonged to a deleted account; it has been restored with its
              earlier projects.
            </p>
          )}
          <p className="text-sm text-gray-700">
            Share this password with {result.email} directly -- it will not be shown again.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700">Temporary password</label>
            <CopyableSecret value={result.password} />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleDone}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell label="New auditor" onClose={onClose}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">New auditor</h3>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="new-first-name" className="block text-sm font-medium text-gray-700">
              First name
            </label>
            <input
              id="new-first-name"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="new-last-name" className="block text-sm font-medium text-gray-700">
              Last name
            </label>
            <input
              id="new-last-name"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div>
          <label htmlFor="new-email" className="block text-sm font-medium text-gray-700">
            Email address
          </label>
          <input
            id="new-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
              Temporary password
            </label>
            <button
              type="button"
              onClick={() => setPassword(generatePassword())}
              className="text-xs font-medium text-blue-600 hover:text-blue-500"
            >
              Generate
            </button>
          </div>
          <PasswordField id="new-password" value={password} onChange={setPassword} autoComplete="new-password" />
          <p className="mt-1 text-xs text-gray-500">
            At least 8 characters. Share it with the auditor directly -- there is no invite email.
          </p>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-[#F8F8FF]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {createMutation.isLoading ? 'Creating…' : 'Create account'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ResetPasswordModal({ targetUser, onClose, onDone }) {
  const [password, setPassword] = useState(() => generatePassword());
  // Set on success; while it holds a value the modal shows the result panel instead
  // of the form (Anshul, 2026-09-26 review item a -- same reasoning as the create
  // modal above: a reset password shown only in a hidden field is easy to lose).
  const [done, setDone] = useState(false);

  const resetMutation = useMutation(() => usersAPI.update(targetUser.id, { password }), {
    onSuccess: () => {
      toast.success(`${targetUser.email}'s password was reset`);
      setDone(true);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not reset the password')),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    resetMutation.mutate();
  };

  const label = `Reset password for ${targetUser.first_name} ${targetUser.last_name}`;

  if (done) {
    return (
      <ModalShell label={label} onClose={onDone}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Password reset</h3>
          <button type="button" onClick={onDone} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-700">
            Share this password with {targetUser.email} directly -- it will not be shown again.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700">New temporary password</label>
            <CopyableSecret value={password} />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onDone}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell label={label} onClose={onClose}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{label}</h3>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700">
              New temporary password
            </label>
            <button
              type="button"
              onClick={() => setPassword(generatePassword())}
              className="text-xs font-medium text-blue-600 hover:text-blue-500"
            >
              Generate
            </button>
          </div>
          <PasswordField id="reset-password" value={password} onChange={setPassword} autoComplete="new-password" />
          <p className="mt-1 text-xs text-gray-500">
            This signs {targetUser.email} out everywhere. Share the new password with them directly.
          </p>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-[#F8F8FF]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={resetMutation.isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {resetMutation.isLoading ? 'Resetting…' : 'Reset password'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteConfirmModal({ targetUser, onClose, onDeleted }) {
  const deleteMutation = useMutation(() => usersAPI.remove(targetUser.id), {
    onSuccess: () => {
      toast.success(`${targetUser.email} was deleted`);
      onDeleted();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete the account')),
  });

  const label = `Delete ${targetUser.first_name} ${targetUser.last_name}?`;

  return (
    <ModalShell label={label} onClose={onClose}>
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{label}</h3>
      </div>
      <div className="px-6 py-4">
        <p className="text-sm text-gray-700">
          {targetUser.email} will no longer be able to sign in. Their projects and reports are kept.
        </p>
      </div>
      <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-[#F8F8FF]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => deleteMutation.mutate()}
          disabled={deleteMutation.isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
        >
          {deleteMutation.isLoading ? 'Deleting…' : 'Delete account'}
        </button>
      </div>
    </ModalShell>
  );
}

function RejectConfirmModal({ targetUser, onClose, onRejected }) {
  const rejectMutation = useMutation(() => usersAPI.reject(targetUser.id), {
    onSuccess: () => {
      toast.success(`${targetUser.email}'s request was rejected`);
      onRejected();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not reject the request')),
  });

  const label = `Reject ${targetUser.first_name} ${targetUser.last_name}'s request?`;

  return (
    <ModalShell label={label} onClose={onClose}>
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{label}</h3>
      </div>
      <div className="px-6 py-4">
        <p className="text-sm text-gray-700">
          {targetUser.email} will not be able to sign in. They would need to request an account again.
        </p>
      </div>
      <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-[#F8F8FF]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => rejectMutation.mutate()}
          disabled={rejectMutation.isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
        >
          {rejectMutation.isLoading ? 'Rejecting…' : 'Reject request'}
        </button>
      </div>
    </ModalShell>
  );
}

/** "Waiting for approval" section (Anshul, 2026-09-26): self-registered requests, at
 * the top of the page, only rendered when there is at least one -- an empty section
 * nobody can act on is exactly the dead-control CLAUDE.md's 2026-09-06 rule bans. */
function PendingApprovalsSection({ pending, onApprove, approvingId, onReject }) {
  if (pending.length === 0) return null;

  return (
    <div className="mb-8 bg-white shadow-sm border border-amber-200 rounded-lg overflow-hidden">
      <div className="px-6 py-4 bg-amber-50 border-b border-amber-200">
        <h2 className="text-sm font-semibold text-amber-900">
          Waiting for approval ({pending.length})
        </h2>
      </div>
      <ul className="divide-y divide-gray-200">
        {pending.map((row) => (
          <li key={row.id} className="px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {row.first_name} {row.last_name}
              </p>
              <p className="text-sm text-gray-500">{row.email}</p>
              <p className="text-xs text-gray-400">Requested {formatDate(row.requested_at)}</p>
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => onApprove(row)}
                disabled={approvingId === row.id}
                className="inline-flex items-center gap-1 text-green-700 hover:text-green-900 font-medium disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" />
                Approve
              </button>
              <button
                type="button"
                onClick={() => onReject(row)}
                className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 font-medium"
              >
                <XMarkIcon className="h-4 w-4" />
                Reject
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function UsersPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const { data, isLoading, error } = useQuery(['admin-users'], () => usersAPI.list(), {
    select: (res) => ({
      users: res.data?.users || [],
      pending: res.data?.pending || [],
    }),
  });

  const users = data?.users || [];
  const pending = data?.pending || [];
  const invalidate = () => {
    queryClient.invalidateQueries(['admin-users']);
    // The Navbar badge polls on its own timer, but approving/rejecting here should
    // not leave a stale count showing until that timer next fires.
    queryClient.invalidateQueries(['admin-users-pending-count']);
  };

  const approveMutation = useMutation((row) => usersAPI.approve(row.id), {
    onSuccess: (_response, row) => {
      toast.success(`${row.email} can now sign in`);
      invalidate();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not approve the request')),
  });

  return (
    <div className="min-h-screen bg-[#F8F8FF]">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Users</h1>
            <p className="text-gray-600">Admin accounts and the auditors you have created for them.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <UserPlusIcon className="h-4 w-4" />
            New auditor
          </button>
        </div>

        {!isLoading && !error && (
          <PendingApprovalsSection
            pending={pending}
            onApprove={(row) => approveMutation.mutate(row)}
            approvingId={approveMutation.isLoading ? approveMutation.variables?.id : null}
            onReject={setRejectTarget}
          />
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : error ? (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Failed to load users')}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-500">No accounts yet.</p>
          </div>
        ) : (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#F8F8FF]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projects</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((rowUser) => {
                  const isAdmin = rowUser.role === 'ADMIN';
                  return (
                    <tr key={rowUser.id} className="hover:bg-[#F8F8FF]">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {rowUser.first_name} {rowUser.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rowUser.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            isAdmin ? 'bg-indigo-100 text-indigo-800' : 'bg-teal-100 text-teal-900'
                          }`}
                        >
                          {isAdmin ? 'Admin' : 'Auditor'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rowUser.project_count}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(rowUser.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {/* Anshul's UI rule: no dead controls. An admin row renders no actions --
                            the backend reserves both PATCH and DELETE for CUSTOMER accounts, so a
                            button here could only ever 403. */}
                        {!isAdmin && (
                          <div className="flex justify-end gap-4">
                            <button
                              type="button"
                              onClick={() => setResetTarget(rowUser)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                            >
                              <KeyIcon className="h-4 w-4" />
                              Reset password
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(rowUser)}
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 font-medium"
                            >
                              <TrashIcon className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <NewAuditorModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            invalidate();
          }}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          targetUser={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => {
            setResetTarget(null);
            invalidate();
          }}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          targetUser={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            invalidate();
          }}
        />
      )}
      {rejectTarget && (
        <RejectConfirmModal
          targetUser={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onRejected={() => {
            setRejectTarget(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}
